import { useEffect, useState } from 'react';

import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import {
    API_GET_CUSTOMER_STATE,
    API_GET_FORMAL_OFFERS,
    API_GET_FUNDING_REQUIREMENT,
    API_GET_LOAN_DOCUMENTS
} from '@api/lendingAPI/LendingAPI.actions';
import { tracking } from '@iwoca/frontend-tracking-library';
import { Link } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import AllRequirementsCompletedStep from './AllRequirementsCompletedStep/AllRequirementsCompletedStep';
import {
    deserialiseFundingRequirements,
    TFundingRequirements
} from './deserialiseFundingRequirements';
import styles from './FundingSteps.module.css';
import { getStepItem } from './getStepItem';
import LoadingOverlay from './LoadingOverlay';
import {
    TFundingRequirementsSteps,
    orderFundingRequirements
} from './orderFundingRequirements';
import { deserialiseSignedDocuments } from './signDocumentAction/SignDocumentsHelper';
import { useIsInSelfServeSplitTest } from '../../../customHooks/useIsInSelfServeSplitTest';
import { SET_DRAWDOWN_DATA } from '../../drawDown/DrawDown.actions';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import { FormalOffer } from '../../myOffers/MyOffers.types';
import messages from '../fundingRequirementsView/FundingRequirementsView.messages';

const mapStateToProps = (state: RootState) => ({
    funding_requirements: state.api.funding_requirement.data,
    customer_state: state.api.customer_state.data,
    formal_offers: state.api.formal_offers.data,
    is_funding_requirements_pending: state.api.funding_requirement.pending,
    is_loan_documents_pending: state.api.loan_documents.pending,
    loan_documents: state.api.loan_documents.data
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getFundingRequirements: ({
        amount,
        offerId,
        productType
    }: {
        amount: number;
        offerId: string;
        productType: string;
    }) => {
        dispatch({
            type: API_GET_FUNDING_REQUIREMENT,
            amount,
            offerId,
            productType
        });
    },
    getLoanDocuments: ({
        amount,
        offerId,
        productType
    }: {
        amount: number;
        offerId: string;
        productType: string;
    }) => {
        dispatch({
            type: API_GET_LOAN_DOCUMENTS,
            productType,
            formalOfferId: offerId,
            amount: amount
        });
    },
    getCustomerState: () => {
        dispatch({
            type: API_GET_CUSTOMER_STATE
        });
    },
    getFormalOffers: () => {
        dispatch({
            type: API_GET_FORMAL_OFFERS
        });
    },
    setDrawdownData: ({
        amount,
        offerId
    }: {
        amount: number;
        offerId: string;
    }) => {
        dispatch({ type: SET_DRAWDOWN_DATA, amount, offerId });
    }
});

type TFundingSteps = {
    formalOffer: FormalOffer;
    drawdownAmount: number;
    sweep?: string;
    isInterestOnlyExtension?: boolean;
    isAccessUser: boolean;
    setHideBackButton?: (hideBackButton: boolean) => void;
} & ReturnType<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

type TStepsUpdateResult = {
    incompleteSteps: TFundingRequirementsSteps;
    completedSteps: TFundingRequirementsSteps;
};

export function updateOnfidoCompletedSteps(
    incompleteSteps: TFundingRequirementsSteps,
    completedSteps: TFundingRequirementsSteps
): TStepsUpdateResult {
    // While all customers are required to complete standard Onfido, the UK funnel teams
    // will start requiring customers with a larger loan to go through a more advanced video flow.
    // Customers who have completed standard onfido, but with a "Required" onfido video check requirement
    // should see the Onfido requirement as incomplete.
    const hasOutstandingOnfidoVideoVerification = incompleteSteps.some(
        (step) => step.type === 'ONFIDO_VIDEO_VERIFICATION'
    );
    const hasOutstandingOnfidoVerification = incompleteSteps.some(
        (step) => step.type === 'ONFIDO_VERIFICATION'
    );

    // If both are incomplete, show only one of them in the incomplete list
    if (
        hasOutstandingOnfidoVideoVerification &&
        hasOutstandingOnfidoVerification
    ) {
        incompleteSteps = incompleteSteps.filter(
            (step) => step.type !== 'ONFIDO_VIDEO_VERIFICATION'
        );
    }

    if (
        hasOutstandingOnfidoVideoVerification ||
        hasOutstandingOnfidoVerification
    ) {
        completedSteps = completedSteps.filter(
            (step) =>
                step.type !== 'ONFIDO_VIDEO_VERIFICATION' &&
                step.type !== 'ONFIDO_VERIFICATION'
        );
    }

    // If both are completed, remove one from the completed list
    completedSteps = completedSteps.filter(
        (step) => step.type !== 'ONFIDO_VIDEO_VERIFICATION'
    );

    return { incompleteSteps, completedSteps };
}

function isStepIncomplete(step: TFundingRequirementsSteps[0]): boolean {
    if (step.status === 'COMPLETED') return false;
    return true;
}

export function deserialiseWaitForOtherGuarantorsFundingRequirements(
    funding_requirements: TFundingRequirements['data']
) {
    return (
        funding_requirements
            ?.find(
                (item) =>
                    item.recommended_resolution_method ===
                    'wait_for_other_guarantors'
            )
            ?.resolution_methods?.flatMap(
                (method) =>
                    method.items?.map((guarantor) => ({
                        type: method.type!.toUpperCase(),
                        status: guarantor.status.toUpperCase(),
                        pg_name: guarantor.name
                    })) || []
            ) || []
    );
}

const FundingSteps = ({
    formalOffer,
    drawdownAmount,
    sweep,
    getFundingRequirements,
    getCustomerState,
    getLoanDocuments,
    getFormalOffers,
    setDrawdownData,
    funding_requirements,
    loan_documents,
    customer_state,
    formal_offers,
    is_funding_requirements_pending,
    is_loan_documents_pending,
    isInterestOnlyExtension,
    isAccessUser,
    setHideBackButton
}: TFundingSteps) => {
    const [activeTab, setActiveTab] = useState<number>(1);
    const isInSelfServeTest = useIsInSelfServeSplitTest();

    const [filteredIncompleteSteps, setFilteredIncompleteSteps] = useState<
        string[] | null
    >(null);
    let loadingFundingRequirements = true;
    let fundingRequirements = null;
    let loanDocuments = null;
    let waitForOtherGuarantorsFundingRequirements = null;
    let allRequirements = null;

    const selfServeTestBranch = useSplitTestBranch({
        test: 'de_self_serve_split_test_dec_2025',
        enabled: Boolean(
            formalOffer.max_amount && formalOffer.max_amount <= 15000
        ),
        suspense: false
    });
    const isInDESelfServeTest = selfServeTestBranch === 'INCLUDE';

    useEffect(() => {
        getCustomerState();
    }, [getCustomerState]);

    useEffect(() => {
        getFormalOffers();
    }, [getFormalOffers]);

    useEffect(() => {
        getFundingRequirements({
            amount: drawdownAmount,
            offerId: formalOffer.offer_id!,
            productType: formalOffer.product_type!
        });

        getLoanDocuments({
            amount: drawdownAmount,
            offerId: formalOffer.offer_id!,
            productType: formalOffer.product_type!
        });

        setDrawdownData({
            amount: drawdownAmount,
            offerId: formalOffer.offer_id!
        });
    }, [
        formalOffer,
        drawdownAmount,
        getFundingRequirements,
        setDrawdownData,
        getLoanDocuments
    ]);

    const triggerRefetchFundingRequirements = () => {
        getFundingRequirements({
            amount: drawdownAmount,
            offerId: formalOffer.offer_id!,
            productType: formalOffer.product_type!
        });
    };

    useEffect(() => {
        if (selfServeTestBranch && filteredIncompleteSteps) {
            tracking.ampli.fundingStepsViewed({
                self_serve_test_branch: selfServeTestBranch,
                incomplete_funding_steps: filteredIncompleteSteps,
                incomplete_funding_steps_amount: filteredIncompleteSteps.length
            });
        }
    }, [selfServeTestBranch, filteredIncompleteSteps]);
    if (
        funding_requirements &&
        funding_requirements.length > 0 &&
        customer_state &&
        formal_offers
    ) {
        fundingRequirements =
            deserialiseFundingRequirements(funding_requirements);

        waitForOtherGuarantorsFundingRequirements = isAccessUser
            ? null
            : deserialiseWaitForOtherGuarantorsFundingRequirements(
                  funding_requirements
              );
    }

    if (
        customer_state &&
        loan_documents &&
        loan_documents.required_loan_documents
    ) {
        loanDocuments = deserialiseSignedDocuments(
            customer_state,
            loan_documents
        );
    }
    //if funding requirements is pending:false, show error
    if (
        !is_funding_requirements_pending &&
        !is_loan_documents_pending &&
        (!fundingRequirements || !loanDocuments)
    ) {
        loadingFundingRequirements = false;
        return <FallbackErrorModal logToDataDog={false} />;
    }

    let incompleteSteps: TFundingRequirementsSteps = [];
    let incompleteRequiredSteps: TFundingRequirementsSteps = [];
    let completedSteps: TFundingRequirementsSteps = [];
    let hasCompletedAllRequirements = false;

    if (
        fundingRequirements &&
        !is_funding_requirements_pending &&
        loanDocuments
    ) {
        //put all funding requirements in one list, by order of priority eg. Give us a Call should show last.
        allRequirements = orderFundingRequirements(
            fundingRequirements,
            loanDocuments,
            waitForOtherGuarantorsFundingRequirements
        );
        loadingFundingRequirements = false;

        incompleteSteps = allRequirements.filter(isStepIncomplete);

        incompleteRequiredSteps = incompleteSteps.filter(
            (incompleteStep) =>
                // Applicant call is still shown as required when in pending state.
                // This logic is in the ApplicantCallAction component.
                incompleteStep.type === 'APPLICANT_CALL' ||
                incompleteStep.status !== 'PENDING'
        );

        completedSteps = allRequirements.filter(
            (requirement: TFundingRequirementsSteps[0]) =>
                requirement.status === 'COMPLETED'
        );

        const updatedSteps = updateOnfidoCompletedSteps(
            incompleteSteps,
            completedSteps
        );
        incompleteSteps = updatedSteps.incompleteSteps;
        if (!filteredIncompleteSteps) {
            setFilteredIncompleteSteps(
                incompleteSteps.map((step) => {
                    return step.type;
                })
            );
        }
        completedSteps = updatedSteps.completedSteps;

        if (isInSelfServeTest || isInDESelfServeTest) {
            completedSteps = completedSteps.filter(
                (step) => step.type !== 'APPLICANT_CALL'
            );
        }

        hasCompletedAllRequirements = incompleteSteps.length === 0;

        if (isAccessUser && hasCompletedAllRequirements && setHideBackButton) {
            setHideBackButton(true);
        }
    }

    return (
        <div className={styles.container}>
            <div>
                <Link
                    className={
                        activeTab === 1
                            ? hasCompletedAllRequirements
                                ? styles.noIncompleteTabLink
                                : styles.activeIncompleteTabLink
                            : hasCompletedAllRequirements
                              ? styles.noIncompleteTabLink_inactive
                              : styles.incompleteTabLink
                    }
                    onClick={() => setActiveTab(1)}
                >
                    <FormattedMessage {...messages.incompleteTabTitle} />
                    <span
                        className={
                            hasCompletedAllRequirements
                                ? styles.incompleteCountContainer_complete
                                : styles.incompleteCountContainer
                        }
                    >
                        {incompleteRequiredSteps.length}
                    </span>
                </Link>
                <Link
                    className={
                        activeTab === 2
                            ? styles.activeCompleteTabLink
                            : styles.completeTabLink
                    }
                    onClick={() => setActiveTab(2)}
                >
                    <FormattedMessage {...messages.completeTabTitle} />
                    <span className={styles.completedCountContainer}>
                        {completedSteps.length}
                    </span>
                </Link>
                <hr className={styles.divider} />
            </div>

            {(loadingFundingRequirements ||
                is_funding_requirements_pending) && (
                <LoadingOverlay>
                    <FormattedMessage {...messages.loadingText} />
                </LoadingOverlay>
            )}

            {hasCompletedAllRequirements && (
                <div>
                    <AllRequirementsCompletedStep
                        offerId={formalOffer.offer_id}
                        amount={drawdownAmount}
                        sweep={sweep}
                        isInterestOnlyExtension={isInterestOnlyExtension}
                        isAccessUser={isAccessUser}
                    />
                </div>
            )}

            {activeTab === 1 && (
                <div>
                    {incompleteSteps.map(
                        (step: TFundingRequirementsSteps[0], index) => {
                            if (
                                (isInSelfServeTest || isInDESelfServeTest) &&
                                incompleteSteps.length > 1 &&
                                step.type === 'APPLICANT_CALL'
                            ) {
                                return null;
                            }
                            return (
                                <div
                                    id={`funding-step-${index}-${step.type}`}
                                    key={`funding-step-${index}-${step.type}`}
                                    className={styles.fundingStepsContainer}
                                >
                                    {getStepItem({
                                        step,
                                        formalOffer,
                                        triggerRefetchFundingRequirements:
                                            triggerRefetchFundingRequirements
                                    })}
                                </div>
                            );
                        }
                    )}
                </div>
            )}

            {activeTab === 2 && (
                <div>
                    {completedSteps.map(
                        (step: TFundingRequirementsSteps[0], index) => (
                            <div
                                id={`funding-step-${index}-${step.type}`}
                                key={`funding-step-${index}-${step.type}`}
                                className={styles.fundingStepsContainer}
                            >
                                {getStepItem({
                                    step,
                                    formalOffer
                                })}
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(FundingSteps);
